import React from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

/**
 * Edytowalna tabela progów mocy — wspólna dla marż i dla stawek montażu (instalacji w kWp, magazynu w kWh).
 *
 * DLACZEGO WSPÓLNA: obie tabele mają identyczną mechanikę (zakres kWp + wartość,
 * ostatni wiersz otwarty od góry). Dwie kopie rozjechałyby się przy pierwszej
 * poprawce, a to jest miejsce, w którym literówka kończy się złą kwotą na
 * ofercie dla klienta końcowego.
 *
 * PUSTE POLE ZNACZY „NIE USTAWIŁEM" i nie jest tym samym co zero. Zero to
 * „montaż za darmo" albo „zerowa marża" i tak trafi na dokument; puste pole
 * mówi liczącemu, żeby tej pozycji nie doliczał. Dlatego pola mogą zostać puste
 * i nie są zerowane na siłę.
 *
 * UKŁAD: nagłówek kolumn stoi NAD polami, a zakres „od–do" jest jedną kolumną
 * z myślnikiem w środku, nie dwiema osobnymi. Wcześniej dwa pola bez podpisu
 * wyglądały jak dwie niezależne liczby i trzeba było zgadywać, co znaczą.
 */

/** Wąskie pole liczbowe. Wydzielone, bo w wierszu powtarza się cztery razy. */
const Pole = ({ wartosc, onChange, disabled, placeholder, etykieta, szerokosc = 'w-[4.5rem]' }) => (
  <input
    type="number" min="0" step="any" disabled={disabled}
    value={wartosc ?? ''} placeholder={placeholder} aria-label={etykieta}
    onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
    className={`${szerokosc} px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-sm
                text-center tabular-nums
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                disabled:bg-gray-50 disabled:text-gray-400`}
  />
)

const Naglowek = ({ children }) => (
  <span className="block text-xs font-semibold text-blue-700">
    {children}
  </span>
)

const TabelaProgow = ({
  progi,
  onChange,
  kolumny,          // [{ klucz, naglowek, jednostka, placeholder }]
  disabled,
  nazwaZakresu = 'Moc instalacji',
  // Jednostka zakresu: kWp dla instalacji, kWh dla magazynu energii (runda 6).
  jednostkaZakresu = 'kWp',
}) => {
  const ustawPole = (i, klucz, wartosc) => {
    onChange(progi.map((p, idx) => (idx === i ? { ...p, [klucz]: wartosc } : p)))
  }

  const usun = (i) => onChange(progi.filter((_, idx) => idx !== i))

  const dodaj = () => {
    const ostatni = progi[progi.length - 1]
    // Nowy wiersz startuje tam, gdzie kończy się poprzedni — dziura w tabeli
    // znaczy, że instalacja o mocy z luki nie dostanie żadnej stawki.
    const od = ostatni ? (ostatni.do ?? Number(ostatni.od) + 5) : 0
    const pusty = { od, do: null }
    kolumny.forEach(({ klucz }) => { pusty[klucz] = null })
    onChange([...progi, pusty])
  }

  return (
    <div className="rounded-xl border border-blue-100 overflow-hidden max-w-lg">
      {/* Pasek nagłówków na niebieskim tle — oddziela opisy od pól do wpisania.
          Bez niego cztery liczby w rzędzie wyglądały jak jedna długa lista. */}
      <div className="flex items-center gap-3 px-3 py-2.5 bg-blue-50 border-b border-blue-100">
        <div className="w-[11.5rem] flex-shrink-0">
          <Naglowek>{nazwaZakresu}</Naglowek>
        </div>
        {kolumny.map(({ klucz, naglowek }) => (
          <div key={klucz} className="w-[9.5rem] flex-shrink-0">
            <Naglowek>{naglowek}</Naglowek>
          </div>
        ))}
        <div className="flex-1" aria-hidden="true" />
      </div>

      <div className="divide-y divide-gray-100">
        {progi.map((p, i) => (
          <div key={i} className="flex items-center gap-3 px-3 py-2 hover:bg-blue-50/40 transition-colors">
            {/* Zakres jako JEDNA kolumna: „od – do" czyta się jak przedział,
                dwa osobne pola czytały się jak dwie niezależne liczby. */}
            <div className="w-[11.5rem] flex-shrink-0 flex items-center gap-1.5">
              <Pole
                wartosc={p.od} disabled={disabled} etykieta={`Próg ${i + 1}, od (${jednostkaZakresu})`}
                onChange={(v) => ustawPole(i, 'od', v)}
              />
              <span className="text-gray-300 select-none">–</span>
              <Pole
                wartosc={p.do} disabled={disabled} etykieta={`Próg ${i + 1}, do (${jednostkaZakresu})`}
                placeholder="∞"
                onChange={(v) => ustawPole(i, 'do', v)}
              />
              <span className="text-xs text-gray-400 whitespace-nowrap">{jednostkaZakresu}</span>
            </div>

            {kolumny.map(({ klucz, jednostka, placeholder }) => (
              <div key={klucz} className="w-[9.5rem] flex-shrink-0 flex items-center gap-1.5">
                <Pole
                  wartosc={p[klucz]} disabled={disabled} placeholder={placeholder}
                  etykieta={`Próg ${i + 1}, ${klucz}`} szerokosc="w-24"
                  onChange={(v) => ustawPole(i, klucz, v)}
                />
                {jednostka && (
                  <span className="text-xs text-gray-400 whitespace-nowrap">{jednostka}</span>
                )}
              </div>
            ))}

            <button
              type="button" onClick={() => usun(i)} disabled={disabled}
              className="w-8 h-8 ml-auto flex-shrink-0 flex items-center justify-center rounded-lg
                         text-gray-300 hover:text-red-600 hover:bg-red-50
                         transition-colors disabled:opacity-40"
              aria-label={`Usuń próg ${i + 1}`}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="px-3 py-2.5 bg-gray-50 border-t border-gray-100">
        <button
          type="button" onClick={dodaj} disabled={disabled}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                     text-sm font-medium text-blue-700 hover:bg-blue-100
                     transition-colors disabled:opacity-40"
        >
          <PlusIcon className="w-4 h-4" />
          Dodaj próg
        </button>
        <span className="ml-3 text-xs text-gray-400">
          Puste pole = nie doliczamy. Pusta górna granica (∞) = „i więcej".
        </span>
      </div>
    </div>
  )
}

export default TabelaProgow
